import { faEye, faPen, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CD80DDetailsForm } from ".";
import { RegimeTypes } from "../../../config/filingConstants";
import { IFormITR1 } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";

const CD80DDetails: React.FC = () => {
  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);
  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);

  const { values, setFieldValue } = useFormikContext<IFormITR1>();
  const RegimeType = values?.FilingReturnInfo?.RegimeType || RegimeTypes.NEW_TAX_REGIME;

  const MedicalInsuranceDetails = values.CommonDeductions.ICDMedical80D;

  const handleTotalCalculation = (section: string, amount: number) => {
    const _Total = Object.entries(values.DeductUndChapVIA).reduce((curr, [key, value]) => {
      if (key === section) {
        return curr + amount;
      }
      return curr + value;
    }, 0);

    setFieldValue("TotalChapVIADeductions", _Total);
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>
            80D - Medical Insurance and Preventive Health Checkup
            <small>
              Deductions for Medical Insurance or Preventive Health Check-Up fees or Medical
              Expenditure incurred by you.
            </small>
          </h5>
          <PreventEditComponent>
            {MedicalInsuranceDetails ? (
              <Button
                variant="warning"
                size="sm"
                className="text-white"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPen} />
                Edit
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddFormCanvas(() => true)}
                disabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            )}
          </PreventEditComponent>

          {isFilingReadOnly && (
            <Button
              variant="warning"
              size="sm"
              className="text-white"
              onClick={() => setShowAddFormCanvas(() => true)}
            >
              <FASvgIcon icon={faEye} />
              View
            </Button>
          )}
        </div>

        {MedicalInsuranceDetails ? (
          <dl className="eds-list eds-dl eds-inline-between">
            <RenderListItem
              title={"80D - Total medical insurance"}
              sum={MedicalInsuranceDetails.TotalSection80D}
            />
            <RenderListItem
              title={"80D - Total preventive health check up cost"}
              sum={MedicalInsuranceDetails.TotalHealthCheckup}
            />
            <RenderListItem
              title={"80D - Total medical expenditure"}
              sum={MedicalInsuranceDetails.TotalExpenditure}
            />
          </dl>
        ) : null}
      </section>

      <CD80DDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(MedicalHealthData) => {
          if (
            MedicalHealthData &&
            (MedicalHealthData.ForParents || MedicalHealthData.ForSelfAndFamily)
          ) {
            let MaxLimit = 0;
            const SelfSenior = MedicalHealthData.SelfAndFamilySeniorCitizen;
            const ParentSenior = MedicalHealthData.ParentSeniorCitizen;
            if (SelfSenior && ParentSenior) {
              MaxLimit = 100000;
            } else if ((SelfSenior && !ParentSenior) || (!SelfSenior && ParentSenior)) {
              MaxLimit = 75000;
            } else {
              MaxLimit = 50000;
            }

            let MI = Number(MedicalHealthData.TotalSection80D);
            if (MI < MaxLimit) {
              MI = MI + Number(MedicalHealthData.TotalHealthCheckup);
            }

            const _TotalSection80D = Math.min(MI, MaxLimit);

            setFieldValue("CommonDeductions.ICDMedical80D", MedicalHealthData);
            setFieldValue("DeductUndChapVIA.Section80D", Number(_TotalSection80D));
            handleTotalCalculation("Section80D", _TotalSection80D);
          } else {
            setFieldValue("CommonDeductions.ICDMedical80D", undefined);
            setFieldValue("DeductUndChapVIA.Section80D", 0);
            handleTotalCalculation("Section80D", 0);
          }
          setShowAddFormCanvas(() => false);
        }}
        formValues={MedicalInsuranceDetails}
      />
    </>
  );
};

export default CD80DDetails;

const RenderListItem = ({ title, sum = "" }: { title: string; sum: string }) => {
  const shouldDisplay = sum !== "" && Number(sum) > 0;

  return (
    <>
      {shouldDisplay && (
        <div className="eds-list--item">
          <dt>{title}</dt>
          <dd>
            <span>
              <PriceDisplay price={sum} />
            </span>
          </dd>
        </div>
      )}
    </>
  );
};
